import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { LegacyPricingArchiveItem } from '@wrapforce/shared/schemas/pricing';
import { Button } from '@/components/ui/button';
import { useLocaleFormatters } from '@/lib/format';
import { useDismissLegacyPricingBanner, useLegacyPricingArchive } from './pricing-v2/api';

function sourceTableLabel(t: TFunction<'settings'>, sourceTable: LegacyPricingArchiveItem['sourceTable']): string {
  if (sourceTable === 'service_types') return t('pricingBanner.sourceLabel.service');
  if (sourceTable === 'coverage_options') return t('pricingBanner.sourceLabel.coverage');
  return t('pricingBanner.sourceLabel.material');
}

function itemName(t: TFunction<'settings'>, item: LegacyPricingArchiveItem): string {
  const snapshot = item.snapshot;
  if (item.sourceTable === 'materials') {
    const brand = typeof snapshot.brand === 'string' ? snapshot.brand : '';
    const series = typeof snapshot.series === 'string' ? snapshot.series : '';
    const name = `${brand} ${series}`.trim();
    return name || t('pricingBanner.unnamedMaterial');
  }
  return typeof snapshot.name === 'string' && snapshot.name ? snapshot.name : t('pricingBanner.unnamed');
}

function itemOldPrice(t: TFunction<'settings'>, formatMoney: (cents: number) => string, item: LegacyPricingArchiveItem): string {
  const snapshot = item.snapshot;
  if (item.sourceTable === 'coverage_options') {
    const parts: string[] = [];
    if (typeof snapshot.sqft === 'number') parts.push(t('pricingBanner.sqft', { count: snapshot.sqft }));
    if (typeof snapshot.labor_hours === 'number') parts.push(t('pricingBanner.laborHours', { count: snapshot.labor_hours }));
    return parts.length > 0 ? parts.join(', ') : t('pricingBanner.notAvailable');
  }
  if (item.sourceTable === 'materials' && typeof snapshot.cost_per_sqft_cents === 'number') {
    return t('pricingBanner.costPerSqft', { price: formatMoney(snapshot.cost_per_sqft_cents) });
  }
  return t('pricingBanner.notAvailable');
}

export function LegacyPricingBanner({ workspaceId }: { workspaceId: string | null }) {
  const { t } = useTranslation('settings');
  const { formatMoney } = useLocaleFormatters();
  const [expanded, setExpanded] = useState(false);
  const { data } = useLegacyPricingArchive(workspaceId);
  const dismiss = useDismissLegacyPricingBanner(workspaceId);

  if (!data || data.items.length === 0 || data.dismissedAt) return null;

  return (
    <div className="border-b border-warning/30 bg-warning/10 px-6 py-3 text-sm text-warning">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p>
          {t('pricingBanner.migratedNotice', { count: data.items.length })}
        </p>
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => setExpanded((v) => !v)}>
            {expanded ? t('pricingBanner.hide') : t('pricingBanner.viewThem')}
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={() => dismiss.mutate()} disabled={dismiss.isPending}>
            {t('pricingBanner.dismiss')}
          </Button>
        </div>
      </div>

      {expanded ? (
        <div className="mt-3 flex flex-col gap-2 border-t border-warning/30 pt-3">
          {data.items.map((item) => (
            <div key={item.id} className="rounded-[var(--radius-card)] border border-warning/30 bg-surface px-3 py-2 text-ink">
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="text-xs font-bold uppercase tracking-wide text-muted">{sourceTableLabel(t, item.sourceTable)}</span>
                <span className="font-semibold">{itemName(t, item)}</span>
                <span className="text-xs text-muted">{t('pricingBanner.oldPrice', { price: itemOldPrice(t, formatMoney, item) })}</span>
              </div>
              <p className="mt-1 text-xs text-muted">{item.reason}</p>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
